// <Chip> — status / filter / value chip with non-color cue.
//
// Tokens: radius.chip (4px per playbook), color.semantic, font.role.label,
// space[1..2].
//
// Per a11y-plan.md A4 / DESIGN_POLICY.md §5: every status chip must include a
// non-color cue (Material Symbol). Pressable chips expose `aria-pressed`.

import type { ReactNode } from 'react';
import { tokens } from '../tokens';
import { Icon, type IconName } from './Icon';

export type ChipVariant = 'neutral' | 'info' | 'success' | 'warning' | 'error' | 'tertiary';

export interface ChipProps {
  label: ReactNode;
  /** Optional leading Material Symbol; encouraged for status chips (A4). */
  icon?: IconName;
  variant?: ChipVariant;
  /** When true, chip is interactive (`role="button"`); else inert. */
  pressable?: boolean;
  pressed?: boolean;
  selected?: boolean;
  onClick?(): void;
  /** Required when label is icon-only; recommended otherwise. */
  'aria-label'?: string;
  /** Optional dismiss button. Adds an `×` button + `aria-label="Remove …"`. */
  onDismiss?: () => void;
  className?: string;
  'data-testid'?: string;
}

const VARIANT_BG: Record<ChipVariant, string> = {
  neutral: tokens.color.surface[2],
  info: tokens.color.semantic.info,
  success: tokens.color.semantic.success,
  warning: tokens.color.semantic.warning,
  error: tokens.color.semantic.danger,
  tertiary: tokens.color.accent.subtle,
};

const VARIANT_FG: Record<ChipVariant, string> = {
  neutral: tokens.color.ink.primary,
  info: tokens.color.ink.inverse,
  success: tokens.color.ink.inverse,
  warning: tokens.color.ink.inverse,
  error: tokens.color.ink.primary,
  tertiary: tokens.color.ink.primary,
};

export function Chip({
  label,
  icon,
  variant = 'neutral',
  pressable = false,
  pressed,
  selected,
  onClick,
  'aria-label': ariaLabel,
  onDismiss,
  className,
  'data-testid': testId,
}: ChipProps) {
  const Tag = pressable ? 'button' : 'span';
  const interactive = pressable || Boolean(onDismiss);
  return (
    <Tag
      type={pressable ? 'button' : undefined}
      role={pressable ? 'button' : selected != null ? 'option' : undefined}
      aria-pressed={pressable ? Boolean(pressed) : undefined}
      aria-selected={selected}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={pressable ? onClick : undefined}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        background: VARIANT_BG[variant],
        color: VARIANT_FG[variant],
        border: variant === 'neutral'
          ? `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`
          : '1px solid transparent',
        borderRadius: tokens.radius.chip,
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.role.label.size,
        lineHeight: tokens.font.role.label.lineHeight,
        cursor: interactive ? 'pointer' : 'default',
        // Touch target: pressable chips meet 32 px (rowDense); a 40 px hit
        // target is the parent's responsibility for inline chips.
        minHeight: pressable ? tokens.density.rowDense : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {icon ? <Icon name={icon} size={16} decorative /> : null}
      <span>{label}</span>
      {onDismiss ? (
        <span
          role="button"
          tabIndex={0}
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onDismiss();
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: tokens.space[3],
            height: tokens.space[3],
            cursor: 'pointer',
          }}
        >
          ×
        </span>
      ) : null}
    </Tag>
  );
}
